import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { sendOrdersToCentralFillApi, validateCentralFillEligibilityApi } from 'api/centralFill';

import { EligibilityResultsTable } from '../SendToCentralFillResults';

const PHASE = {
  VALIDATING: 'VALIDATING',
  VALIDATED: 'VALIDATED',
  SENDING: 'SENDING',
};

// The eligibility response carries partner/reason; drug name and rx# come from
// the original bulk rows.
const enrichResult = ({ result, lineItem }) => ({
  ...result,
  drugName: result.drugName || lineItem.medicationName,
  rxNumber: result.rxNumber || lineItem.rxNumber,
});

const CentralFillSendDialog = ({ open, onClose, lineItems, onConfirm }) => {
  const [phase, setPhase] = useState(PHASE.VALIDATING);
  const [validationResults, setValidationResults] = useState([]);

  const lineItemsById = useMemo(
    () => new Map(lineItems.map((item) => [item.lineItemId, item])),
    [lineItems]
  );

  const loadValidation = useCallback(async () => {
    setPhase(PHASE.VALIDATING);
    try {
      const eligibility = await validateCentralFillEligibilityApi(
        lineItems.map(({ lineItemId }) => lineItemId)
      );
      setValidationResults(eligibility?.results || []);
    } catch (error) {
      enqueueSnackbar('Failed to validate eligibility', {
        variant: 'error',
        autoHideDuration: 4000,
      });
      setValidationResults([]);
    } finally {
      setPhase(PHASE.VALIDATED);
    }
  }, [lineItems]);

  useEffect(() => {
    if (open && lineItems.length) {
      loadValidation();
    }
  }, [open, lineItems, loadValidation]);

  const enrichedResults = useMemo(
    () =>
      validationResults.map((result) =>
        enrichResult({ result, lineItem: lineItemsById.get(result.lineItemId) || {} })
      ),
    [validationResults, lineItemsById]
  );

  const eligibleResults = useMemo(
    () => enrichedResults.filter(({ isEligible }) => isEligible),
    [enrichedResults]
  );
  const ineligibleResults = useMemo(
    () => enrichedResults.filter(({ isEligible }) => !isEligible),
    [enrichedResults]
  );

  // Single table, eligible rows first — Confirm sends the eligible orders, so the
  // actionable set leads and the ineligible exclusions follow as reference.
  const orderedResults = useMemo(
    () => [...eligibleResults, ...ineligibleResults],
    [eligibleResults, ineligibleResults]
  );

  const totalOrders = lineItems.length;
  const eligibleCount = eligibleResults.length;

  const isValidating = phase === PHASE.VALIDATING;
  const isSending = phase === PHASE.SENDING;

  // Send: fire the API, confirm to the parent (clear selection + refresh), show a
  // success snackbar, and close — no separate result screen.
  const handleSend = async () => {
    const eligibleIds = eligibleResults.map(({ lineItemId }) => lineItemId);
    if (!eligibleIds.length) {
      return;
    }

    setPhase(PHASE.SENDING);
    try {
      const response = await sendOrdersToCentralFillApi(eligibleIds);
      const sent = response?.totalSent ?? eligibleIds.length;
      enqueueSnackbar(
        `${sent} eligible order${
          sent === 1 ? '' : 's'
        } sent to compounding. Check the compounding queue for processing status.`,
        { variant: 'success', autoHideDuration: 4000 }
      );
      if (onConfirm) {
        onConfirm(response);
      }
      onClose();
    } catch (error) {
      enqueueSnackbar('Failed to send orders to compounding', {
        variant: 'error',
        autoHideDuration: 4000,
      });
      setPhase(PHASE.VALIDATED);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='lg'
      PaperProps={{ sx: { maxHeight: '88vh', borderRadius: 3, maxWidth: 1150 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
      >
        <Box>
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            Eligibility Validation Result
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Review the eligibility status of the selected orders.
          </Typography>
        </Box>
        <IconButton
          aria-label='close'
          onClick={onClose}
          size='small'
          sx={{
            color: 'text.secondary',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isValidating ? (
          <Stack
            spacing={2}
            alignItems='center'
            justifyContent='center'
            sx={{ flex: 1, minHeight: 220 }}
            aria-live='polite'
          >
            <CircularProgress />
            <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
              Validating selected orders…
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Checking compounding eligibility rules
            </Typography>
          </Stack>
        ) : (
          // Warning stays fixed; the single results table fills the rest and scrolls
          // internally, so the dialog body never grows an outer scrollbar.
          <Box
            sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2.5 }}
            aria-live='polite'
          >
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: '0 10px 10px 0',
                px: 2,
                py: 1.25,
                background: 'linear-gradient(135deg, #fffbeb 0%, #fefce8 100%)',
                border: '1px solid rgba(245,158,11,0.2)',
                borderLeftWidth: '4px',
                borderLeftColor: '#f59e0b',
              }}
            >
              <WarningAmberIcon sx={{ color: '#d97706', fontSize: '1.1rem', flexShrink: 0 }} />
              <Typography
                sx={{ fontSize: '0.78rem', lineHeight: 1.5, color: '#92400e', fontWeight: 500 }}
              >
                Confirming sends the eligible orders to the compounding pharmacy and removes them
                from the bulk queue. Ineligible orders remain in the bulk queue.
              </Typography>
            </Box>

            <Box
              sx={{ flex: 1, minHeight: 0, display: 'flex', '& > div': { flex: 1, minHeight: 0 } }}
            >
              <EligibilityResultsTable rows={orderedResults} maxHeight='100%' />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', bgcolor: '#f8f9fb' }}>
        {isValidating ? (
          <span />
        ) : (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
              {eligibleCount}
            </Box>
            {' of '}
            <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
              {totalOrders}
            </Box>
            {` order${totalOrders === 1 ? '' : 's'} will be sent to compounding`}
          </Typography>
        )}

        <Stack direction='row' spacing={1.5}>
          <Button
            variant='outlined'
            color='inherit'
            onClick={onClose}
            disabled={isSending}
            sx={{ borderRadius: 2, borderColor: 'divider', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={isSending}
            variant='contained'
            onClick={handleSend}
            disabled={isValidating || isSending || eligibleCount === 0}
            sx={{
              bgcolor: '#201d52',
              borderRadius: 2,
              '&:hover': { bgcolor: '#2c2870' },
            }}
          >
            Confirm
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

CentralFillSendDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string,
      medicationName: PropTypes.string,
      rxNumber: PropTypes.string,
    })
  ).isRequired,
  onConfirm: PropTypes.func,
};

CentralFillSendDialog.defaultProps = {
  onConfirm: undefined,
};

export default CentralFillSendDialog;
